import React, { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { CloudArrowUpIcon, DocumentIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { projectService } from '../services/projectService';
import { storageService } from '../services/storageService';
import { emailService } from '../services/emailService';
import { formatFileSize } from '../lib/format';
import { ErrorNote, Field, Modal } from './ui';

interface CreateReleaseModalProps {
  projectId: string;
  suggestedVersion?: string;
  onClose: () => void;
  onSuccess: () => void;
}

/** Sends the "new release" e-mail with a retry action in the toast. */
export const notifyNewRelease = async (
  projectId: string,
  releaseId: string,
  t: (key: string, vars?: Record<string, string | number>) => string
) => {
  const toastId = toast.loading(t('releases.notifying'));
  try {
    const result = await emailService.sendNewReleaseNotification(projectId, releaseId);
    if (result.failed.length > 0) {
      toast.error(t('releases.notifyPartial', { sent: result.sent.length, failed: result.failed.length }), {
        id: toastId,
        duration: 8000,
      });
    } else {
      toast.success(t('releases.notified'), { id: toastId });
    }
  } catch (err) {
    console.error('Failed to send release notification:', err);
    toast.error(
      (instance) => (
        <span className="flex items-center gap-3">
          {t('releases.notifyFailed')}
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => {
              toast.dismiss(instance.id);
              notifyNewRelease(projectId, releaseId, t);
            }}
          >
            {t('common.retry')}
          </button>
        </span>
      ),
      { id: toastId, duration: 10000 }
    );
  }
};

const CreateReleaseModal: React.FC<CreateReleaseModalProps> = ({ projectId, suggestedVersion, onClose, onSuccess }) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [version, setVersion] = useState(suggestedVersion || '');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const pickFile = (files: FileList | null) => {
    if (files && files[0]) {
      setFile(files[0]);
      setError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    if (!file) {
      setError(t('releases.fileRequired'));
      return;
    }
    setError('');
    setLoading(true);
    try {
      const fileUrl = await storageService.uploadFile(file, projectId, setProgress);
      const releaseId = await projectService.createRelease(
        projectId,
        version,
        title,
        description,
        file.name,
        file.size,
        fileUrl,
        user.uid
      );
      toast.success(t('releases.published', { version: version.trim() }));
      onSuccess();
      notifyNewRelease(projectId, releaseId, t);
    } catch (err) {
      console.error('Error creating release:', err);
      setError(t('releases.publishFailed'));
    } finally {
      setLoading(false);
      setProgress(0);
    }
  };

  return (
    <Modal
      title={t('releases.new')}
      description={t('releases.newDescription')}
      onClose={onClose}
      busy={loading}
      size="lg"
      footer={
        <>
          <button type="button" onClick={onClose} disabled={loading} className="btn btn-secondary">
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            form="create-release"
            disabled={loading || !file || !version.trim() || !title.trim()}
            className="btn btn-primary"
          >
            {loading ? t('releases.uploading', { progress: Math.round(progress) }) : t('releases.publish')}
          </button>
        </>
      }
    >
      <form id="create-release" onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-[160px_1fr]">
          <Field label={t('releases.version')} htmlFor="release-version">
            <input
              id="release-version"
              className="input font-mono"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              required
              placeholder="1.0.0"
            />
          </Field>
          <Field label={t('releases.title')} htmlFor="release-title">
            <input
              id="release-title"
              className="input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              placeholder={t('releases.titlePlaceholder')}
            />
          </Field>
        </div>

        <Field label={t('releases.notes')} htmlFor="release-notes" hint={t('releases.notesHint')}>
          <textarea
            id="release-notes"
            className="input"
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>

        <div>
          <span className="field-label">{t('releases.file')}</span>
          <div
            role="button"
            tabIndex={0}
            onClick={() => !loading && inputRef.current?.click()}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && inputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              pickFile(e.dataTransfer.files);
            }}
            className={`relative flex cursor-pointer items-center gap-4 overflow-hidden rounded-xl border-2 border-dashed px-4 py-5 transition-colors ${
              dragging
                ? 'border-signal-500 bg-signal-50 dark:bg-signal-900/20'
                : 'border-gray-200 hover:border-primary-400 dark:border-gray-700 dark:hover:border-primary-500'
            }`}
          >
            <input ref={inputRef} type="file" className="hidden" onChange={(e) => pickFile(e.target.files)} />
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400">
              {file ? <DocumentIcon className="h-5 w-5" /> : <CloudArrowUpIcon className="h-5 w-5" />}
            </span>
            {file ? (
              <div className="min-w-0 flex-1">
                <p className="truncate font-mono text-sm text-gray-900 dark:text-gray-100">{file.name}</p>
                <p className="text-xs text-gray-500">
                  {formatFileSize(file.size)} — {t('releases.changeFile')}
                </p>
              </div>
            ) : (
              <div className="text-sm">
                <p className="font-medium text-gray-800 dark:text-gray-200">{t('releases.dropFile')}</p>
                <p className="text-xs text-gray-500">{t('releases.orBrowse')}</p>
              </div>
            )}
            {loading && (
              <div className="absolute inset-x-0 bottom-0 h-1 bg-gray-100 dark:bg-gray-800">
                <div className="h-full bg-signal-500 transition-[width] duration-300" style={{ width: `${progress}%` }} />
              </div>
            )}
          </div>
        </div>

        {error && <ErrorNote>{error}</ErrorNote>}
      </form>
    </Modal>
  );
};

export default CreateReleaseModal;
